import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { IconCheck, IconMinus } from "@tabler/icons-react";

import { cn } from "@/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";

export const checkboxVariants = cva(
  "peer cursor-pointer rounded-sm border data-[state=checked]:border-none data-[state=indeterminate]:border-none",
  {
    variants: {
      variant: {
        primary:
          "border-white/50 bg-white/20 data-[state=checked]:bg-linear-to-br data-[state=checked]:from-primary-400 data-[state=checked]:to-secondary-400 data-[state=checked]:text-primary-foreground data-[state=indeterminate]:bg-linear-to-br data-[state=indeterminate]:from-primary-400 data-[state=indeterminate]:to-secondary-400 data-[state=indeterminate]:text-primary-foreground",
        tertiary:
          "border-border data-[state=checked]:bg-tertiary-400 data-[state=checked]:text-white data-[state=indeterminate]:bg-tertiary-400 data-[state=indeterminate]:text-white",
      },
      size: {
        default: "h-5 w-5",
        sm: "h-4 w-4",
        lg: "h-6 w-6",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  },
);

type CheckboxVariantProps = VariantProps<typeof checkboxVariants>;

const iconSizeMap = {
  default: "h-4 w-4",
  sm: "h-3 w-3",
  lg: "h-5 w-5",
} as const;

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> &
    CheckboxVariantProps
>(({ className, variant, size, ...props }, ref) => {
  const iconSize = iconSizeMap[size ?? "default"];

  return (
    <CheckboxPrimitive.Root
      ref={ref}
      className={cn(checkboxVariants({ variant, size, className }))}
      {...props}
    >
      <CheckboxPrimitive.Indicator className="flex items-center justify-center text-current">
        {props.checked === "indeterminate" ? (
          <IconMinus className={iconSize} />
        ) : (
          <IconCheck className={iconSize} />
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
});
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
